/**
 * Platform admin data: stats, schools, users, each school's admins and the
 * three AI feature flags, loaded together on mount and on pull-to-refresh.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiRequest } from '../../../../services/apiClient';
import { showDialog } from '../../../../utils/dialogs';
import { useToast } from '../../../../context/ToastContext';
import type { Translate } from '../platformAdminFormat';
import type { PlatformStats, PlatformUser, School, SchoolAdmin } from '../platformAdminTypes';
import {
  AGENTIC_CHAT_TOGGLE,
  ANALYSIS_STREAMING_TOGGLE,
  CHAT_STREAMING_TOGGLE,
  useFeatureToggle,
} from './useFeatureToggle';

export function usePlatformAdminData(t: Translate) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [schools, setSchools] = useState<School[]>([]);
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [platformUsers, setPlatformUsers] = useState<PlatformUser[]>([]);
  const [adminsBySchool, setAdminsBySchool] = useState<Record<number, SchoolAdmin[]>>({});

  const chatStreaming = useFeatureToggle(CHAT_STREAMING_TOGGLE);
  const analysisStreaming = useFeatureToggle(ANALYSIS_STREAMING_TOGGLE);
  const agenticChat = useFeatureToggle(AGENTIC_CHAT_TOGGLE);
  const loadChatStreaming = chatStreaming.load;
  const loadAnalysisStreaming = analysisStreaming.load;
  const loadAgenticChat = agenticChat.load;

  const loadPlatformData = useCallback(async () => {
    try {
      setLoading(true);

      const [statsData, schoolsData, usersData] = await Promise.all([
        apiRequest<PlatformStats>({ endpoint: '/schools/platform/stats' }),
        apiRequest<School[]>({ endpoint: '/schools/' }),
        apiRequest<PlatformUser[]>({ endpoint: '/schools/platform/users' }),
      ]);

      setStats(statsData);
      setSchools(schoolsData);
      setPlatformUsers(usersData);

      // Load admins for each school
      const adminMap: Record<number, any[]> = {};
      for (const s of schoolsData) {
        try {
          const admins = await apiRequest<any[]>({ endpoint: `/schools/${s.id}/admins` });
          adminMap[s.id] = admins;
        } catch (e) {
          console.warn('Failed to load admins for school', s.id, e);
          adminMap[s.id] = [];
        }
      }
      setAdminsBySchool(adminMap);

      // Load chat streaming, analysis streaming and agentic chat settings
      await loadChatStreaming();
      await loadAnalysisStreaming();
      await loadAgenticChat();

    } catch (error) {
      console.error('Error loading platform data:', error);
      showDialog(t('common.error'), t('platformAdmin.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t, loadChatStreaming, loadAnalysisStreaming, loadAgenticChat]);

  useEffect(() => {
    loadPlatformData();
  }, [loadPlatformData]);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadPlatformData();
    setRefreshing(false);
  };

  const resendAdminInvite = async (schoolId: number, adminId: number) => {
    try {
      await apiRequest({ endpoint: `/schools/${schoolId}/admins/${adminId}/resend-invite`, method: 'POST' });
      showToast(t('common.success'), 'success');
      // Refresh admins list for this school
      try {
        const admins = await apiRequest<any[]>({ endpoint: `/schools/${schoolId}/admins` });
        setAdminsBySchool((prev) => ({ ...prev, [schoolId]: admins }));
      } catch { }
    } catch (e: any) {
      const message = e?.message || t('common.error');
      showDialog(message);
    }
  };

  return {
    loading,
    refreshing,
    schools,
    stats,
    platformUsers,
    adminsBySchool,
    chatStreaming,
    analysisStreaming,
    agenticChat,
    loadPlatformData,
    onRefresh,
    resendAdminInvite,
  };
}

export type PlatformAdminData = ReturnType<typeof usePlatformAdminData>;
